

import { useEffect, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import {
  CheckIcon,
  CircleNotchIcon,
  EnvelopeIcon,
  FolderOpenIcon,
  ShieldIcon,
  ShoppingCartIcon,
  UserPlusIcon,
  UsersIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { permissionsApi } from '@/lib/api/permissions.api';
import { useOrganizationStore } from '@/store/organization.store';
import { useAccountType } from '@/hooks/useAccountType';
import { useInviteUsersMutation } from '@/hooks/mutations/useOrganizationInvitesMutations';
import { useSubscriptionSeats } from '@/hooks/useSubscriptionSeats';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { PermissionSet } from '@scrambl/shared';
import { useProjectsQuery } from '@/hooks/queries/useProjectsQuery';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { createLogger } from '@/lib/logger';
import { getErrorMessage, getErrorStatus } from '@/lib/api/error-utils';

const logger = createLogger('components/layout/InviteTeamMembersDialog');

export function InviteTeamMembersDialog({
  isOpen,
  onClose,
  onSuccess
}: {
  isOpen: boolean;
onClose: () => void;
  onSuccess?: () => void;
}) {
  const navigate = useNavigate();
  const [emails, setEmails] = useState('');
  const [permissionSetId, setPermissionSetId] = useState<string>('');
  // Personal accounts get a simplified access picker in place of the business
  // permission-set matrix — "Member" (MEMBER role), "Can manage" (admin-level
  // role), or "Limited" (MEMBER role + the Limited Member permission set, a
  // kid-friendly preset). All three map onto the same invite payload.
  const [personalRole, setPersonalRole] = useState<'MEMBER' | 'ADMIN' | 'LIMITED'>('MEMBER');
  const [permissionSets, setPermissionSets] = useState<PermissionSet[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());

  const { currentOrganization, isOrgAdmin } = useOrganizationStore();
  const { isPersonal } = useAccountType();
  const inviteUsersMutation = useInviteUsersMutation();
  const {
    seatUsage,
    availableSeats,
    fetchSeatUsage,
    subscription
  } = useSubscriptionSeats();
  // Only fetch projects when dialog is open, without custom fields (not needed for selection)
  const { data: projectsResponse, isLoading: isLoadingProjectsQuery } = useProjectsQuery(
    { includeCustomFields: false },
    { enabled: isOpen }
  );
  const projects = projectsResponse?.data || [];

  // Check if current user can set member roles (admin or owner)
  const canSetRoles = isOrgAdmin();

  // Load permission sets and seat usage when dialog opens
  useEffect(() => {
    if (isOpen && currentOrganization?.id) {
      // Fetch seat usage — business only. Personal accounts have no subscription
      // or seats, so there is nothing to fetch.
      if (!isPersonal) {
        fetchSeatUsage();
      }

      // Load permission sets if user can set roles. Business accounts use the
      // full permission-set picker; personal accounts only need the sets loaded
      // so the "Limited" choice can resolve the Limited Member set by systemKey
      // (no picker UI — the role dropdown drives it).
      if (canSetRoles) {
        setIsLoadingPermissions(true);
        permissionsApi.getPermissionSets(currentOrganization.id)
          .then(sets => {
            setPermissionSets(sets.filter(set => set.isActive));
            // Default to Basic Access if available
            const basicAccess = sets.find(set => set.systemKey === 'basic_access');
            if (basicAccess) {
              setPermissionSetId(basicAccess.id);
            }
          })
          .catch(error => {
            logger.error('Failed to load permission sets:', error);
            toast.error('Failed to load permission sets');
          })
          .finally(() => {
            setIsLoadingPermissions(false);
          });
      }
    }
  }, [isOpen, currentOrganization?.id, canSetRoles, isPersonal, fetchSeatUsage]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setEmails('');
      setSelectedProjects(new Set());
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate emails
    if (!emails.trim()) {
      newErrors.emails = 'Please enter at least one email address';
    } else {
      const emailList = emails.split(',').map(email => email.trim());
      const invalidEmails = emailList.filter(email => !validateEmail(email));

      if (invalidEmails.length > 0) {
        newErrors.emails = `Invalid email format: ${invalidEmails.join(', ')}`;
      }

      // Check if there are enough seats available
      if (subscription && availableSeats !== undefined) {
        if (availableSeats < 0) {
          newErrors.emails = `You are ${Math.abs(availableSeats)} seats over capacity. Please purchase more seats or suspend members before inviting.`;
        } else if (emailList.length > availableSeats) {
          newErrors.emails = `You only have ${availableSeats} seat${availableSeats === 1 ? '' : 's'} available. Please purchase more seats or invite fewer members.`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleManageSeats = () => {
    onClose();
    navigate(`/${currentOrganization?.slug}/admin/subscription`);
  };

  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const toggleAllProjects = () => {
    if (selectedProjects.size === projects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(projects.map(p => p.id)));
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void handleSubmit(e as unknown as FormEvent);
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!validateForm() || !currentOrganization?.id) return;

    // Double-check seat availability
    if (subscription && availableSeats <= 0) {
      toast.error(availableSeats < 0
        ? `You are ${Math.abs(availableSeats)} seats over capacity. Please purchase additional seats first.`
        : 'No seats available. Please purchase additional seats first.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const emailList = emails.split(',').map(email => email.trim());

      // Personal accounts pick the role via the simplified picker; business
      // accounts always invite as MEMBER + a permission set. The "Limited"
      // personal choice invites as MEMBER with the Limited Member set attached.
      const limitedMemberSet = permissionSets.find(
        set => set.systemKey === 'limited_member',
      );
      const finalRole: 'MEMBER' | 'ADMIN' = isPersonal
        ? personalRole === 'ADMIN'
          ? 'ADMIN'
          : 'MEMBER'
        : 'MEMBER';
      const finalPermissionSetId = isPersonal
        ? personalRole === 'LIMITED'
          ? limitedMemberSet?.id
          : undefined
        : canSetRoles && permissionSetId
          ? permissionSetId
          : undefined;

      // First, invite users to the organization with project metadata
      await inviteUsersMutation.mutateAsync({
        organizationId: currentOrganization.id,
        invites: emailList.map(email => ({
          email,
          role: finalRole,
          permissionSetId: finalPermissionSetId,
          metadata: selectedProjects.size > 0 ? { projectIds: Array.from(selectedProjects) } : undefined,
        }))
      });

      // Reset form
      setEmails('');
      setSelectedProjects(new Set());
      // Reset permission set to default
      const basicAccess = permissionSets.find(set => set.systemKey === 'basic_access');
      if (basicAccess) {
        setPermissionSetId(basicAccess.id);
      }

      // Refresh seat usage (invites invalidation handled by mutation hook)
      try {
        await fetchSeatUsage();
      } catch (refreshError) {
        logger.warn('Failed to refresh seat usage after sending:', refreshError);
      }

      // Close dialog
      onClose();

      // Trigger success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      logger.error('Failed to send invitations:', error);

      const fallbackMessage = 'Failed to send invitations';
      const status = getErrorStatus(error);
      const apiMessage = getErrorMessage(error, fallbackMessage);
      let errorMessage = fallbackMessage;

      if (status === 500) {
        errorMessage = 'The invitation service is temporarily unavailable. Please try again later.';
      } else if (status === 401) {
        errorMessage = 'You don\'t have permission to send invitations. Please contact your administrator.';
      } else if (status === 403) {
        errorMessage = 'Access denied. Please check your permissions or contact your administrator.';
      } else if (status === 400) {
        errorMessage = apiMessage !== fallbackMessage
          ? apiMessage
          : 'Invalid invitation data. Please check the email addresses and try again.';
      } else {
        errorMessage = apiMessage;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate how many emails are being entered
  const emailCount = emails.trim() ? emails.split(',').filter(e => e.trim()).length : 0;
  const hasEnoughSeats = subscription ? emailCount <= availableSeats : true;
  // PERSONAL accounts are free (no subscription, no seats) — seat capping never
  // applies, so the "no seats / purchase more" block must never gate the
  // personal member-invite flow.
  const hasNoSeats = !isPersonal && subscription !== undefined && availableSeats <= 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden">
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-5 pt-5 pb-4 border-b border-border/40">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm">
                <UserPlusIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-base font-semibold">
                  {isPersonal ? 'Invite Members' : 'Invite Team Members'}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isPersonal
                    ? 'Share your space with the people you live and plan with'
                    : 'Send invitations to grow your team'}
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Seat Availability Alert — business only. Personal accounts are
                free (no subscription) so seat/billing language never applies. */}
            {!isPersonal && subscription && seatUsage && (
              <div className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg border text-xs",
                availableSeats <= 0
                  ? "border-destructive/30 bg-destructive/5 text-destructive"
                  : "border-border bg-muted/30"
              )}>
                <UsersIcon className="h-4 w-4 shrink-0" />
                <span className="flex-1">
                  {availableSeats < 0 ? (
                    <>
                      <strong>{Math.abs(availableSeats)}</strong> seats over capacity
                      <span className="ml-1 text-muted-foreground">
                        ({seatUsage.usedSeats} used of {seatUsage.totalSeats})
                      </span>
                    </>
                  ) : (
                    <>
                      <strong>{availableSeats}</strong> of <strong>{seatUsage.totalSeats}</strong> seats available
                      {emailCount > 0 && (
                        <span className="ml-1.5 text-muted-foreground">
                          • Inviting {emailCount}
                        </span>
                      )}
                    </>
                  )}
                </span>
                {availableSeats < 3 && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={handleManageSeats}
                    className="h-auto p-0 text-xs shrink-0"
                  >
                    <ShoppingCartIcon className="h-3 w-3 mr-1" />
                    Add seats
                  </Button>
                )}
              </div>
            )}

            {/* No-seats warning (shown in place of the form when capped) */}
            {hasNoSeats && (
              <div className="flex items-start gap-3 px-3 py-3 rounded-lg border border-destructive/30 bg-destructive/5 text-destructive text-xs">
                <WarningCircleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">
                    {availableSeats < 0
                      ? `You are ${Math.abs(availableSeats)} seats over capacity.`
                      : 'No seats available.'}
                  </p>
                  <p className="text-muted-foreground">
                    Purchase more seats {availableSeats < 0 ? 'or suspend members ' : ''}to invite new members.
                  </p>
                </div>
              </div>
            )}

            {/* Email Input */}
            {!hasNoSeats && (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <EnvelopeIcon className="h-3 w-3" />
                Email Addresses <span className="text-red-500">*</span>
                {emailCount > 0 && (
                  <Badge
                    variant={hasEnoughSeats ? "secondary" : "destructive"}
                    className="ml-1 text-[10px] px-1.5 py-0"
                  >
                    {emailCount}
                  </Badge>
                )}
              </span>
              <input
                type="text"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="team@company.com, john@company.com"
                autoFocus
                disabled={isSubmitting}
                className={cn(
                  "w-full h-9 px-3 text-sm rounded-lg border border-input bg-background outline-none transition-colors",
                  "focus:ring-2 focus:ring-primary/20 focus:border-primary",
                  "placeholder:text-muted-foreground/40 disabled:opacity-50",
                  errors.emails && "border-red-500 focus:ring-red-500/20"
                )}
              />
              <AnimatePresence>
                {errors.emails ? (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-[11px] text-red-500 flex items-center gap-1"
                  >
                    <WarningCircleIcon className="h-3 w-3" />
                    {errors.emails}
                  </motion.p>
                ) : (
                  <p className="text-[11px] text-muted-foreground/60">
                    Separate multiple emails with commas
                  </p>
                )}
              </AnimatePresence>
            </div>
            )}

            {/* Access — personal accounts get a friendly two-choice picker
                ("Member" / "Can manage") in place of the business permission-set
                matrix. Both map onto the existing member roles. */}
            {!hasNoSeats && canSetRoles && isPersonal && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <ShieldIcon className="h-3 w-3" />
                  Access
                </span>
                <Select
                  value={personalRole}
                  onValueChange={value =>
                    setPersonalRole(value as 'MEMBER' | 'ADMIN' | 'LIMITED')
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MEMBER" className="text-sm">
                      Member
                    </SelectItem>
                    <SelectItem value="ADMIN" className="text-sm">
                      Can manage
                    </SelectItem>
                    <SelectItem value="LIMITED" className="text-sm">
                      Limited
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground/60">
                  {personalRole === 'ADMIN'
                    ? 'Can manage your space, people, and everyone’s access'
                    : personalRole === 'LIMITED'
                      ? 'Great for kids — sees shared plans, manages their own tasks'
                      : 'Can view and work on shared projects and tasks'}
                </p>
              </div>
            )}

            {/* Access Level Selection (business) */}
            {!hasNoSeats && canSetRoles && !isPersonal && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <ShieldIcon className="h-3 w-3" />
                  Access Level
                </span>
                <Select
                  value={permissionSetId}
                  onValueChange={setPermissionSetId}
                  disabled={isLoadingPermissions || isSubmitting}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder={isLoadingPermissions ? "Loading..." : "Choose access level"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel className="text-xs">Available Levels</SelectLabel>
                      {permissionSets.map(set => (
                        <SelectItem key={set.id} value={set.id} className="text-sm">
                          {set.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {permissionSetId && (
                  <p className="text-[11px] text-muted-foreground/60">
                    {permissionSets.find(s => s.id === permissionSetId)?.description}
                  </p>
                )}
              </div>
            )}

            {/* Project Selection */}
            {!hasNoSeats && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <FolderOpenIcon className="h-3 w-3" />
                  Project Access
                  <span className="text-muted-foreground/50">(optional)</span>
                  {selectedProjects.size > 0 && (
                    <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
                      {selectedProjects.size}
                    </Badge>
                  )}
                </span>
                {projects.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAllProjects}
                    className="text-[11px] text-primary hover:underline"
                    disabled={isSubmitting}
                  >
                    {selectedProjects.size === projects.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>

              {isLoadingProjectsQuery ? (
                <div className="flex items-center justify-center py-6">
                  <CircleNotchIcon className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-6 text-xs text-muted-foreground">
                  No projects available
                </div>
              ) : (
                <div className="h-[140px] rounded-lg border border-input overflow-y-auto">
                  <div className="p-2 space-y-0.5">
                    {projects.map((project) => {
                      const isSelected = selectedProjects.has(project.id);
                      return (
                        <label
                          key={project.id}
                          htmlFor={`project-${project.id}`}
                          className={cn(
                            "flex items-center gap-2.5 py-1.5 px-2 rounded-md cursor-pointer transition-colors",
                            "hover:bg-muted/50",
                            isSelected && "bg-primary/5",
                            isSubmitting && "pointer-events-none opacity-50"
                          )}
                        >
                          <input
                            type="checkbox"
                            id={`project-${project.id}`}
                            checked={isSelected}
                            onChange={() => toggleProjectSelection(project.id)}
                            disabled={isSubmitting}
                            className="h-3.5 w-3.5 rounded border-input accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{project.name}</p>
                          </div>
                          {isSelected && (
                            <CheckIcon className="h-3.5 w-3.5 text-primary shrink-0" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground/60">
                {isPersonal
                  ? 'They’ll be able to see and help with the selected projects'
                  : 'Users will be added as contributors to selected projects'}
              </p>
            </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="px-5 py-3 border-t border-border/40 bg-muted/20">
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] text-muted-foreground">
                ⌘+Enter to send
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={isSubmitting || (canSetRoles && !isPersonal && isLoadingPermissions) || !hasEnoughSeats || hasNoSeats}
                  className="min-w-[100px]"
                >
                  {isSubmitting ? (
                    <>
                      <CircleNotchIcon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="h-3.5 w-3.5 mr-1.5" />
                      Send Invites
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
